import Seo from "@/components/Seo";
import React from "react";
import {
  WhyHero, Heading, Reveal, PainGrid, FlowSequence, CompareTable,
  CheckList, LabelChips, WhyFinalCTA, PaymentPageMock, PayoutMock,
} from "@/components/why/WhyParts";
import {
  LINKS_PAINS, LINKS_FLOW, LINKS_COMPARE, LINKS_VENDOR_CARDS, LINKS_CUSTOMER_BENEFITS,
} from "@/data/content";

export default function WhyPaymentLinks() {
  return (
    <main>
      <Seo path="/why-workpay/workpay-vs-payment-links" />
      <WhyHero
        eyebrow="Why Workpay"
        title={<>A payment link collects money. <span className="text-gradient">Workpay protects the project around it.</span></>}
        subtitle="Basic payment links are built for checkout. Workpay is built for service projects with milestones, approvals, tracking, and payment clarity."
        secondaryLabel="See How It Works"
        visual={<PaymentPageMock />}
      />

      <section className="py-16 sm:py-24 bg-[#f7faf8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><Heading overline="The gap" title="The problem with basic payment links" /></Reveal>
          <PainGrid items={LINKS_PAINS} />
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Heading overline="More than a link" title="Workpay is more than a link." subtitle="It is a project payment workflow." />
          </Reveal>
          <Reveal delay={0.08}><FlowSequence steps={LINKS_FLOW} /></Reveal>
          <Reveal delay={0.12}>
            <div className="mt-12 max-w-sm mx-auto"><PayoutMock /></div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#f7faf8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal><Heading overline="Side by side" title="Basic Payment Links vs Workpay" /></Reveal>
          <Reveal delay={0.08}><CompareTable colA="Basic Payment Links" colB="Workpay" rows={LINKS_COMPARE} /></Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Heading
              overline="Vendor-first"
              title={<>You don't need another checkout link. <span className="text-gradient">You need a better way to start projects.</span></>}
              subtitle="Vendors are not only asking customers to pay. They are asking customers to commit to a clear project workflow."
            />
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 max-w-xl mx-auto"><CheckList items={LINKS_VENDOR_CARDS} /></div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 sm:py-24 bg-[#f7faf8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Heading
              overline="For customers"
              title="Customers feel safer when the project is visible."
              subtitle="Workpay gives customers more than a payment page. They can see project terms, track progress, approve milestones, and raise issues clearly."
            />
          </Reveal>
          <LabelChips items={LINKS_CUSTOMER_BENEFITS.map((b) => ({ icon: "ShieldCheck", label: b }))} />
        </div>
      </section>

      <WhyFinalCTA
        title={<>Make Workpay your default <span className="text-[#21c55e]">project payment link.</span></>}
        subtitle="For vendors who want payment clarity and customers who want project visibility."
      />
    </main>
  );
}
